/**
 * Tâche 1349 rev2 (Eric 2026-06-30) — Différence mot à mot entre la version
 * d'origine (machine) et la version réécrite à la main.
 *
 * Première version : toute la phrase retouchée passait en bleu foncé. Eric a
 * relevé que le bleu doit désigner CE QUI A CHANGÉ, pas la phrase entière —
 * sinon la couleur ne dit plus rien sur l'endroit à relire.
 *
 * Algorithme : plus longue sous-suite commune (LCS) sur les mots. Les mots
 * communs restent « egal », les mots absents de la version d'avant sont
 * « ajoute », ceux qui ont disparu sont « supprime ».
 */

const MOTS = /(\s+)/;

function decouper(texte) {
  return String(texte || "").split(MOTS).filter((t) => t !== "");
}

/**
 * @returns {Array<{type: "egal"|"ajoute"|"supprime", texte: string}>}
 */
export function diffMots(avant, apres) {
  const a = decouper(avant);
  const b = decouper(apres);
  // Table LCS. Les transcriptions sont découpées en tours courts ; au-delà
  // d'un texte déraisonnable on renonce au détail plutôt que de figer l'écran.
  if (a.length * b.length > 400000) {
    return [{ type: "ajoute", texte: String(apres || "") }];
  }
  const t = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      t[i][j] = a[i] === b[j] ? t[i + 1][j + 1] + 1 : Math.max(t[i + 1][j], t[i][j + 1]);
    }
  }
  const sortie = [];
  const pousser = (type, texte) => {
    const dernier = sortie[sortie.length - 1];
    if (dernier && dernier.type === type) dernier.texte += texte;
    else sortie.push({ type, texte });
  };
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      pousser("egal", a[i]);
      i += 1;
      j += 1;
    } else if (t[i + 1][j] >= t[i][j + 1]) {
      pousser("supprime", a[i]);
      i += 1;
    } else {
      pousser("ajoute", b[j]);
      j += 1;
    }
  }
  while (i < a.length) { pousser("supprime", a[i]); i += 1; }
  while (j < b.length) { pousser("ajoute", b[j]); j += 1; }
  // Une suppression faite uniquement d'espaces n'apprend rien au relecteur.
  return sortie.filter((s) => !(s.type === "supprime" && !s.texte.trim()));
}
